import { Workspace } from '../../../apis/api-client';
import WorkspaceAutocomplete from '../../common/WorkspaceAutocomplete';
import { AddIcon, SearchIcon } from '../../icons';
import { Box, Button, InputAdornment, TextField, Tooltip, Typography } from '../../ui';

interface GroupsDirectoryHeaderProps {
  totalCount: number;
  /** Account-wide view only: the page-level workspace filter. */
  showWorkspaceFilter: boolean;
  workspaceFilter: Workspace | null;
  onWorkspaceFilterChange: (workspace: Workspace | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** When absent, creation is disabled (no single workspace in scope). */
  createTargetWorkspaceId?: number;
  onCreate: () => void;
}

/**
 * Header strip — title + inline count on the left; workspace filter
 * (account-wide view), search, and New Group on the right. Presentational:
 * the orchestrator owns the paged query, filter state, and create flow.
 */
export default function GroupsDirectoryHeader({
  totalCount,
  showWorkspaceFilter,
  workspaceFilter,
  onWorkspaceFilterChange,
  search,
  onSearchChange,
  createTargetWorkspaceId,
  onCreate,
}: GroupsDirectoryHeaderProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Groups
          </Typography>
          {totalCount > 0 && (
            <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600 }}>
              ({totalCount})
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, flexWrap: 'wrap' }}>
        {showWorkspaceFilter && (
          <Box sx={{ minWidth: 220 }}>
            <WorkspaceAutocomplete value={workspaceFilter} onChange={onWorkspaceFilterChange} />
          </Box>
        )}
        <TextField
          size="small"
          placeholder="Search groups..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 260 }}
        />
        <Tooltip
          title={createTargetWorkspaceId ? '' : 'Select a workspace to create a group'}
          disableHoverListener={!!createTargetWorkspaceId}
        >
          <span>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={onCreate}
              disabled={!createTargetWorkspaceId}
              sx={{
                color: '#fff',
                '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
                px: 3,
              }}
            >
              New Group
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
